import React from "react";
import { Row, Col, Button, Form } from "reactstrap";
import BaseForm from "../../common/BaseForm";
import getVideoRecorderTypes from "../../../services/videoRecorderTypeService";
import ScreenshotModel from "../../models/remotecontrol/ScreenshotModel";

/**
 * Resolve the VideoRecorderType id the on-box screenshot script expects.
 *
 * `video_recorder_type_id` travels to the box verbatim, so it has to stay a VideoRecorderType
 * primary key. A legacy recorder already carries one; a camera-model device carries a brand
 * instead, whose ids do not line up with the type table (AXIS360 is brand 3 / type 8, TVT is
 * brand 13 / type 14), so the id has to be resolved by name.
 *
 * Both the brand name and its kind are tried, in that order, because neither alone covers the
 * table. The name has to come first for TVT, which owns a type row while its kind is "IP". The
 * kind has to be tried next for GENERIC_IP and UNIVIEW, which have no type row of their own but
 * whose kind "IP" does. Resolving to nothing is not harmless: the model requires this field, so
 * the form would refuse to submit with no visible reason.
 *
 * @param {object} videoRecorder - Legacy recorder or camera-model device in the legacy shape.
 * @param {Array<{id: number, name: string}>} deviceTypes - The VideoRecorderType list.
 * @returns {number|undefined} The type id, or undefined when neither name matches a type.
 */
export function resolveDeviceTypeId(videoRecorder, deviceTypes) {
  if (videoRecorder.video_recorder_type_id) return videoRecorder.video_recorder_type_id;
  const types = deviceTypes || [];
  const matched = [videoRecorder.video_recorder_brand_name, videoRecorder.video_recorder_type_name]
    .filter(Boolean)
    .map((wanted) => types.find((type) => type.name === wanted))
    .find((type) => type?.id != null);
  return matched?.id ?? undefined;
}

class ScreenshotForm extends BaseForm {
  state = {
    data: {},
    errors: {},
    deviceTypes: [],
  };
  screenshotModel = new ScreenshotModel();
  schema = this.screenshotModel.getSchema();

  async fetchDeviceTypes() {
    const { data: deviceTypes } = await getVideoRecorderTypes();
    return deviceTypes;
  }

  async componentDidMount() {
    const deviceTypes = await this.fetchDeviceTypes();
    const { videoRecorder } = this.props;
    let data = {};
    if (videoRecorder) {
      data = {
        video_recorder_type_id: resolveDeviceTypeId(videoRecorder, deviceTypes),
        // Left blank when the device's cameras disagree on the suffix, so the operator is never
        // handed one that silently belongs to a camera other than the channel they type in.
        // Legacy recorders carry no such flag and keep prefilling.
        suffix:
          videoRecorder.rtsp_url_suffix_uniform === false ? undefined : videoRecorder.rtsp_url_suffix || undefined,
        username: videoRecorder.username || undefined,
        password: videoRecorder.password || undefined,
        device_ip: videoRecorder.device_ip || undefined,
        device_rtsp_port: videoRecorder.device_rtsp_port || undefined,
        protocol: "tcp",
        subtype: "sub",
      };
    } else {
      data = this.screenshotModel.getDefaultData();
    }
    this.setState({ deviceTypes, data });
  }

  doSubmit = async () => {
    await this.props.handleSubmit(this.state.data);
  };

  render() {
    return (
      <Form>
        <Row>
          <Col md="6">{this.renderSelect("video_recorder_type_id", "Device type", this.state.deviceTypes)}</Col>
          <Col md="6">{this.renderInput("suffix", "RTSP URL suffix")}</Col>
        </Row>
        <Row>
          <Col md="6">{this.renderInput("username", "Username")}</Col>
          <Col md="6">{this.renderInput("password", "Password")}</Col>
        </Row>
        <Row>
          <Col md="6">{this.renderInput("device_ip", "Device IP")}</Col>
          <Col md="3">{this.renderInput("device_rtsp_port", "RTSP port")}</Col>
          <Col md="3">
            {this.renderSelect("protocol", "Protocol", [
              { name: "tcp", id: "tcp" },
              { name: "udp", id: "udp" },
            ])}
          </Col>
        </Row>
        <Row>
          <Col md="6">{this.renderInput("channel", "Channel")}</Col>
          <Col md="6">
            {this.renderSelect("subtype", "subtype", [
              { name: "sub", id: "sub" },
              { name: "main", id: "main" },
            ])}
          </Col>
        </Row>
        <Col>
          <Button
            className="float-right btn-success m-2"
            disabled={this.props.isDisabledButton}
            onClick={this.handleSubmit}
          >
            <i className="fa fa-play mr-2" />
            Screenshot
          </Button>
        </Col>
      </Form>
    );
  }
}

export default ScreenshotForm;
